import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";

import { Heading } from "components/Heading";
import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import { PageHeadings, PageTitles } from "../../constants";
import {
  deleteRedemptionCode,
  getRedemptionCodeDetails,
  licensePlansApi,
  redemptionCodesApi,
  saveRedemptionCode,
  updateRedemptionCode,
} from "../../api/api.service";
import CodeDetails, { StatusBadge } from "./CodeDetails";
import CodeForm from "./CodeForm";
import {
  buildCreateBody,
  buildUpdateBody,
  describeDiscount,
  describeReach,
  draftFromRow,
  rollsOntoName,
  validateDraft,
} from "./draft";
import "./Styles.scss";

/**
 * Admin-managed custom subscription codes.
 *
 * A code lets ops/sales hand a school or district a single string that
 * completes a real, custom-priced, custom-duration purchase themselves,
 * through the same Square checkout a self-serve purchase uses — see
 * Neo-backend's RedemptionCodeService.Redeem(). It can also be a discount for
 * an individual teacher: they enter it at the paywall and pay less for their
 * first term (RedemptionCodeService.ReserveForIndividual). This console only
 * creates and manages the codes; redemption itself happens on the buyer's own
 * billing screen.
 */

/** Whole dollars read as "$500"; cents appear only when there are some. */
const money = (cents) =>
  cents == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);

const FILTERS = [
  {
    key: "status",
    label: "Any status",
    options: [
      ["ACTIVE", "Active"],
      ["INACTIVE", "Inactive"],
      ["EXPIRED", "Expired"],
      ["EXHAUSTED", "Fully used"],
    ],
  },
  {
    key: "visibility",
    label: "Public and private",
    options: [
      ["PUBLIC", "Public"],
      ["PRIVATE", "Private"],
    ],
  },
  {
    key: "audience",
    label: "Everyone",
    options: [
      ["SCHOOL", "Schools"],
      ["DISTRICT", "Districts"],
      ["INDIVIDUAL", "Teachers"],
    ],
  },
];

const UsageMeter = ({ used, limit }) => (
  <div className="rc-usage">
    <span>
      {used} of {limit}
    </span>
    <div className="rc-usage-bar" aria-hidden="true">
      <i style={{ width: `${Math.min((used / Math.max(limit, 1)) * 100, 100)}%` }} />
    </div>
  </div>
);

const emptyDraft = {
  name: "",
  code: "",
  audience: "SCHOOL",
  state: "",
  org: null,
  visibility: "PUBLIC",
  authorizedEmails: [],
  planId: "",
  renewalPlanId: "",
  discountType: "AMOUNT",
  durationWeeks: "",
  legacyMonths: null,
  price: "",
  percentOff: "",
  maxRedemptions: "1",
  activatesAt: "",
  expiresAt: "",
};

const RedemptionCodes = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  const [filters, setFilters] = useState({ search: "", status: "", visibility: "", audience: "" });
  const [searchText, setSearchText] = useState("");
  const filtersSearch = useRef("");

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.status) params.status = filters.status;
      if (filters.visibility) params.visibility = filters.visibility;
      if (filters.audience) params.audience = filters.audience;
      const res = await redemptionCodesApi(params);
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotal(res?.total ?? 0);
    } catch (e) {
      toast.error(e?.message || "Could not load redemption codes.");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // A pause in typing, not every key: each search is a request.
  useEffect(() => {
    const wait = setTimeout(() => {
      setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText }));
      setPage((p) => (filtersSearch.current === searchText ? p : 1));
      filtersSearch.current = searchText;
    }, 250);
    return () => clearTimeout(wait);
  }, [searchText]);

  useEffect(() => {
    licensePlansApi()
      .then((res) => setPlans(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setPlans([]));
  }, []);

  const openCreate = () => setDraft({ ...emptyDraft });

  const openEdit = (row) => setDraft(draftFromRow(row, plans));

  const openDetails = async (row) => {
    setDetailsLoading(true);
    setDetails({ id: row.id });
    try {
      const res = await getRedemptionCodeDetails(row.id);
      setDetails(res?.data ?? res);
    } catch (e) {
      toast.error(e?.message || "Could not load that code.");
      setDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    const problem = validateDraft(
      draft,
      plans.find((p) => String(p.id) === String(draft.planId)),
      money,
    );
    if (problem) {
      toast.error(problem);
      return;
    }

    setSaving(true);
    try {
      if (draft.id) {
        await updateRedemptionCode(draft.id, buildUpdateBody(draft));
        toast.success("Code updated.");
      } else {
        await saveRedemptionCode(buildCreateBody(draft));
        toast.success("Code created.");
      }
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.message || "Could not save that code.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (row.redeemedCount > 0) return;
    if (!window.confirm(`Delete ${row.code}? This cannot be undone.`)) return;
    try {
      await deleteRedemptionCode(row.id);
      toast.success("Code deleted.");
      load();
    } catch (e) {
      toast.error(e?.message || "Could not delete that code.");
    }
  };

  const totalPages = Math.max(Math.ceil(total / 20), 1);
  const isFiltered = Boolean(filters.search || filters.status || filters.visibility || filters.audience);

  return (
    <div className="page-content redemption-codes-page">
      <Helmet>
        <title>{PageTitles.REDEMPTION_CODES}</title>
      </Helmet>
      <Container fluid>
        <div className="rc-page-head">
          <div>
            <Heading heading={PageHeadings.REDEMPTION_CODES} />
            <p>Give a school, district or teacher a set price and term. They redeem the code at their own checkout.</p>
          </div>
          <CustomButton title="Create a code" color="primary" onClick={openCreate} />
        </div>

        <div className="rc-toolbar">
          <div className="rc-search">
            <i className="mdi mdi-magnify" aria-hidden="true" />
            <input
              className="form-control"
              aria-label="Search by name or code"
              placeholder="Search by name or code"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          {FILTERS.map(({ key, label, options }) => (
            <select
              key={key}
              className="form-select"
              aria-label={label}
              value={filters[key]}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((f) => ({ ...f, [key]: value }));
                setPage(1);
              }}
            >
              <option value="">{label}</option>
              {options.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          ))}
          {!loading && (
            <span className="rc-count">
              {total} {total === 1 ? "code" : "codes"}
            </span>
          )}
        </div>

        <div className="rc-list">
          <div className="table-responsive">
            <table className="rc-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Who can use it</th>
                  <th>Deal</th>
                  <th>Used</th>
                  <th>Status</th>
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="rc-table-note">
                      Loading codes…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="rc-table-note">
                      {isFiltered ? "No codes match these filters." : "No codes yet. Create one to give a school its own price."}
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const deal = describeDiscount(row, money);
                  const rollsOnto = rollsOntoName(row, plans);
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="rc-code-text">{row.code}</span>
                        {row.name && <small>{row.name}</small>}
                      </td>
                      <td>
                        {describeReach(row)}
                        <small>{row.visibility === "PRIVATE" ? "Private" : "Public"}</small>
                      </td>
                      <td>
                        {row.plan?.name ?? "—"}
                        {(deal || rollsOnto) && (
                          <small>
                            {deal}
                            {rollsOnto && `${deal ? ", then " : "Then "}${rollsOnto}`}
                          </small>
                        )}
                      </td>
                      <td>
                        <UsageMeter used={row.redeemedCount} limit={row.maxRedemptions} />
                      </td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="rc-row-actions">
                        <button type="button" onClick={() => openDetails(row)} aria-label={`View ${row.code}`}>
                          View
                        </button>
                        <button type="button" onClick={() => openEdit(row)} aria-label={`Edit ${row.code}`}>
                          Edit
                        </button>
                        {row.redeemedCount === 0 ? (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => remove(row)}
                            aria-label={`Delete ${row.code}`}
                          >
                            Delete
                          </button>
                        ) : (
                          <span className="rc-row-gap" aria-hidden="true" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="rc-pages">
            <CustomButton title="Previous" color="light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} />
            <span>
              Page {page} of {totalPages}
            </span>
            <CustomButton
              title="Next"
              color="light"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            />
          </div>
        )}

        {/* ---------------------------------------------------------- create/edit */}
        <CustomModal
          isOpen={!!draft}
          onClose={() => !saving && setDraft(null)}
          toggle={() => !saving && setDraft(null)}
          size="xl"
          contentClassName="rc-modal"
          modalBodyClasses="p-0"
        >
          {draft && (
            <CodeForm
              draft={draft}
              setDraft={setDraft}
              plans={plans}
              money={money}
              saving={saving}
              onSave={save}
              onCancel={() => setDraft(null)}
            />
          )}
        </CustomModal>

        {/* ---------------------------------------------------------- details */}
        <CustomModal
          isOpen={!!details}
          onClose={() => setDetails(null)}
          toggle={() => setDetails(null)}
          size="xl"
          contentClassName="rc-modal"
          modalBodyClasses="p-0"
        >
          {details && (
            <CodeDetails
              code={details}
              loading={detailsLoading}
              plans={plans}
              money={money}
              onClose={() => setDetails(null)}
              onEdit={() => {
                setDetails(null);
                openEdit(details);
              }}
            />
          )}
        </CustomModal>
      </Container>
    </div>
  );
};

export default RedemptionCodes;
